import React from 'react';
import Icon from '../../../components/AppIcon';
import VoiceInputButton from '../../../components/ui/VoiceInputButton';

const TimingSelector = ({ selectedTiming, onSelect, timingContext, onTimingContextChange }) => {
  const timingOptions = [
    { id: 'just-now', label: 'Just Now', icon: 'Clock', description: 'Started moments ago' },
    { id: 'earlier-today', label: 'Earlier Today', icon: 'Sun', description: 'Started today' },
    { id: 'few-days', label: 'Few Days Ago', icon: 'Calendar', description: '2-7 days ago' },
    { id: 'weeks-ago', label: 'Weeks Ago', icon: 'CalendarDays', description: 'More than a week' },
    { id: 'comes-goes', label: 'Comes and Goes', icon: 'Repeat', description: 'On and off' }
  ];

  const handleVoiceInput = (text) => {
    if (onTimingContextChange) {
      const sanitizedText = text?.slice(0, 200);
      onTimingContextChange(sanitizedText);
    }
  };

  const handleTextChange = (e) => {
    const text = e?.target?.value?.slice(0, 200);
    if (onTimingContextChange) {
      onTimingContextChange(text);
    }
  };

  return (
    <div className="space-y-4">
      <div className="mb-6">
        <h3 className="text-lg md:text-xl lg:text-2xl font-heading font-bold text-foreground mb-2">
          When did this start?
        </h3>
        <p className="text-sm md:text-base text-muted-foreground">
          Choose the timing that best fits
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
        {timingOptions?.map((option) => {
          const isSelected = selectedTiming === option?.id;
          return (
            <button
              key={option?.id}
              onClick={() => onSelect(option?.id)}
              className={`
                flex items-start gap-4 p-4 md:p-5 rounded-xl
                border-2 transition-smooth min-h-[90px]
                ${isSelected 
                  ? 'border-primary bg-primary/5 shadow-therapeutic-md' 
                  : 'border-border bg-card hover:border-primary/30 hover:shadow-therapeutic'
                }
                focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background
                active:scale-[0.97]
              `}
              aria-pressed={isSelected}
              aria-label={`${option?.label}: ${option?.description}`}
            >
              <div className={`
                flex items-center justify-center w-12 h-12 rounded-lg flex-shrink-0
                ${isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}
                transition-smooth
              `}>
                <Icon name={option?.icon} size={20} strokeWidth={2} />
              </div>
              <div className="flex-1 text-left">
                <h4 className={`
                  text-sm md:text-base font-semibold mb-1
                  ${isSelected ? 'text-foreground' : 'text-foreground'}
                `}>
                  {option?.label}
                </h4>
                <p className="text-xs md:text-sm text-muted-foreground">
                  {option?.description}
                </p>
              </div>
              {isSelected && (
                <Icon 
                  name="CheckCircle2" 
                  size={20} 
                  className="text-primary flex-shrink-0"
                  strokeWidth={2.5}
                />
              )}
            </button>
          );
        })}
      </div>

      {/* Additional context input for "Comes and Goes" */}
      {selectedTiming === 'comes-goes' && (
        <div className="mt-6 p-4 md:p-5 bg-card border-2 border-primary/30 rounded-xl space-y-3">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Info" size={18} className="text-primary" />
            <h4 className="text-sm md:text-base font-semibold text-foreground">
              Add More Details (Optional)
            </h4>
          </div>
          <p className="text-xs md:text-sm text-muted-foreground mb-3">
            Provide additional context about the pattern, such as duration or frequency
          </p>
          <div className="relative">
            <textarea
              value={timingContext || ''}
              onChange={handleTextChange}
              placeholder="e.g., Although it comes and goes, it's been on for about 2 months..."
              className="w-full min-h-[100px] p-3 pr-12 text-sm md:text-base border-2 border-border rounded-lg
                focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary
                bg-background text-foreground placeholder:text-muted-foreground
                resize-none transition-smooth"
              maxLength={200}
              aria-label="Additional timing context"
            />
            <div className="absolute top-2 right-2">
              <VoiceInputButton
                value={timingContext || ''}
                onChange={handleVoiceInput}
                maxLength={200}
              />
            </div>
          </div>
          <div className="flex justify-between items-center text-xs text-muted-foreground">
            <span>Use voice input or type your details</span>
            <span className={`font-medium ${
              (timingContext?.length || 0) > 180 ? 'text-warning' : ''
            }`}>
              {timingContext?.length || 0}/200
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimingSelector;